'use client';

import { useEffect } from 'react';
import { useTealium } from '@fshr/context/tealium-context';
import { useParams, usePathname } from 'next/navigation';
import { defaultFetcher } from '@fshr/shared';
import useSWR from 'swr';
import { useProfile } from '@profiles/lib/context/profile-context';
import { getConfig } from '@fshr/shared-react';
import { getUpcomingStay } from './get-upcoming-stay';

const ADDITIONAL_PARAMETERS: Record<string, Record<string, string | string[]>> = {
	'find-a-reservation': {
		event_name: 'form_view',
		form_name: 'find a reservation',
	},
	'find-a-reservation/-hotelCode/-confirmationNumber?/-additionalParam?': {
		page_detail: 'confirm_reservation_details',
		event_name: 'form_view',
		form_name: 'confirm reservation details',
	},
	register: {
		page_detail: 'new_profile:welcome',
	},
	'profile/my-stays/-hotelCode/-confirmationNumber?/estimated-total': {
		page_detail: 'reservation_details',
	},
	'profile/my-stays/-hotelCode/-confirmationNumber?/edit-stay': {
		page_detail: 'edit_reservation:request',
		form_name: 'edit reservation',
		event_name: 'form_view',
	},
	'profile/my-stays/-hotelCode/-confirmationNumber?/cancel-stay': {
		page_detail: 'cancel_reservation:request',
		form_name: 'cancel reservation',
		event_name: 'form_view',
	},
	'profile/my-stays/-hotelCode/-confirmationNumber?/departure-transportation': {
		page_detail: 'departure_transportation:request',
		form_name: 'My Stays: Departure Transportation',
		event_name: 'form_view',
	},
	'profile/my-stays/-hotelCode/-confirmationNumber?/arrival-transportation': {
		page_detail: 'arrival_transportation:request',
		form_name: 'My Stays: Arrival Transportation',
		event_name: 'form_view',
	},
	'profile/my-stays/-hotelCode/-confirmationNumber?/welcome-amenities': {
		page_detail: 'arrival_amenities:request',
		form_name: 'My Stays: Amenities Request',
		event_name: 'form_view',
	},
};

const matchPathname = (pathname: string) => {
	const pathParts = pathname.split('/').filter(Boolean);
	const matchedKey = Object.keys(ADDITIONAL_PARAMETERS).find(key => {
		const keyParts = key.split('/');
		if (keyParts.length < pathParts.length) {
			return false;
		}
		let match = true;
		keyParts.forEach((keyPart, keyIndex) => {
			if (
				!(keyPart === pathParts[keyIndex]) &&
				!(keyPart.endsWith('?') || (keyPart.startsWith('-') && pathParts[keyIndex]))
			) {
				match = false;
			}
		});
		return match;
	});
	return matchedKey || '';
};

export default function TealiumTrackPage() {
	const { loadTealium, trackPage } = useTealium();
	const pathname = usePathname();
	const { hotelCode } = useParams<{ hotelCode?: string }>();
	const { FSHR_PUBLIC_TEALIUM_ACCOUNT } = getConfig();
	const { data: pageData, isLoading } = useSWR(
		`${process.env.NEXT_PUBLIC_BFF_BASE_URL}/profile-page/`,
		defaultFetcher,
		{
			revalidateIfStale: false,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
		}
	);

	const { profile, isLoadingProfile, isAuthenticated } = useProfile();

	//Track page on route change
	useEffect(() => {
		//Try again only if we didn't load
		loadTealium(true);
		if (!isLoading && !isLoadingProfile) {
			let userType = isAuthenticated ? 'member' : 'visitor';
			if (profile?.isEmployee) {
				userType = 'employee';
			} else if (profile?.extension?.propertyOwned) {
				userType = 'owner';
			}
			trackPage({
				aa_account:
					FSHR_PUBLIC_TEALIUM_ACCOUNT !== 'prod'
						? 'fshglobaldev'
						: profile?.isEmployee
							? 'fshglobalinternal'
							: 'fshglobal',
				page_name: window.location.pathname,
				site_template: 'profile',
				property_code: hotelCode || 'profile',
				property_name: 'profile',
				property_region: 'corporate',
				upcoming_stay: getUpcomingStay({
					checkInTime: pageData?.upcomingTripCheckIn?.checkInTime,
					isAuthenticated,
				}),
				stay_status: 'na',
				...(ADDITIONAL_PARAMETERS[matchPathname(pathname)] || {}),
				user_type: userType,
				user_id: profile?.uniqueId || '',
				golden_id: profile?.goldenId || 'na',
			});
		}
	}, [pathname, hotelCode, isLoading, isLoadingProfile]);
	return <></>;
}
